import React, { Component, ReactNode } from 'react';
import Dropzone, { DropzoneState, FileRejection } from 'react-dropzone';
import LanguageUtil from '../../util/LanguageUtil';
import AnimatedButton from '../custom/AnimatedButton';
import Icon from 'icons/Icon';
import MessageUtil from '../../util/MessageUtil';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import EnvironmentUtil from '../../util/EnvironmentUtil';

class AchFileUpload extends Component<Props, State> {
  private fileInputRef = React.createRef<HTMLInputElement>();
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public state: State = new State();

  public render(): ReactNode {
    const { disabled, dialog } = this.props;
    const { isDragActive } = this.state;
    const maxSize = this.environmentUtil.getStorageMaxSize();
    const maxSizeInMB = (maxSize / 1048576).toFixed(0);

    return (
      <Dropzone
        disabled={disabled}
        onDrop={this.onDrop}
        onDragEnter={this.handleDragEnter}
        onDragLeave={this.handleDragLeave}
        maxSize={maxSize}
      >
        {({ getRootProps, getInputProps, isDragActive: dropzoneIsDragActive }: DropzoneState) => {
          const active = isDragActive || dropzoneIsDragActive;
          return (
            <div
              {...getRootProps()}
              className={`flex flex-col items-center justify-center w-full h-full p-8 rounded-lg border-2 border-dashed transition-colors ${
                active ? 'border-primary bg-primary/5' : 'border-gray-300 bg-gray-50'
              } cursor-pointer hover:border-primary/50  transition-all duration-200`}
            >
              <input {...getInputProps()} ref={this.fileInputRef} />
              <div className='flex flex-col items-center justify-center gap-y-4 w-full max-w-md'>
                <div className='relative'>
                  <div className='w-24 h-24 flex items-center justify-center'>
                    <div className='flex items-center justify-center text-white'>
                      <Icon icon='import-file-icon' size={80} className='drop-shadow-md' />
                    </div>
                  </div>
                  <div className='absolute -bottom-1 -right-1 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-md'>
                    <div className='flex items-center justify-center text-white w-8 h-8'>
                      <Icon icon='import' />
                    </div>
                  </div>
                </div>

                <div className='flex flex-col items-center gap-y-2'>
                  <span className='text-lg font-semibold text-gray-700'>
                    {LanguageUtil.get({
                      id: 'common.label.dragAndDropFiles',
                      defaultMessage: 'Drag & Drop your files to upload',
                    })}
                  </span>

                  <span className='text-sm text-gray-500'>
                    {LanguageUtil.get(
                      {
                        id: 'common.label.achFileSpecs',
                        defaultMessage: 'All file types supported - Max Size: {maxSize} MB',
                      },
                      { maxSize: maxSizeInMB },
                    )}
                  </span>
                </div>

                <AnimatedButton
                  onClick={(e) => {
                    e.stopPropagation();
                    this.fileInputRef.current?.click();
                  }}
                  icon='import'
                  text={'Select File'}
                  customType='primary'
                  customSize='md'
                  className={`!w-32 ${dialog ? '!text-xs 2xl:!text-sm' : ''}`}
                />
              </div>
            </div>
          );
        }}
      </Dropzone>
    );
  }

  private handleDragEnter = (): void => {
    this.setState({ isDragActive: true });
  };

  private handleDragLeave = (): void => {
    this.setState({ isDragActive: false });
  };

  private handleRejectedFiles = (rejectedFiles: FileRejection[]): void => {
    if (rejectedFiles.length === 0) {
      return;
    }

    const firstRejection = rejectedFiles[0];
    const hasSizeError = firstRejection.errors.some((error) => error.code === 'file-too-large');
    const maxSizeInMB = (this.environmentUtil.getStorageMaxSize() / 1048576).toFixed(0);

    const errorMessage = hasSizeError
      ? LanguageUtil.get(
          {
            id: 'common.fileTooLarge',
            defaultMessage: 'File size exceeds {maxSize} MB limit',
          },
          { maxSize: maxSizeInMB },
        )
      : LanguageUtil.get({
          id: 'messages.error.fileUploadError',
          defaultMessage: 'File upload error occurred',
        });

    MessageUtil.error({
      summary: LanguageUtil.get({
        id: 'common.label.achFile',
        defaultMessage: 'ACH File',
      }),
      detail: errorMessage,
    });

    this.setState({ isDragActive: false });
  };

  private onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
    const { onFileSelect, onFilesSelect } = this.props;

    if (rejectedFiles.length > 0) {
      this.handleRejectedFiles(rejectedFiles);
      return;
    }

    if (acceptedFiles.length === 0) {
      this.setState({ isDragActive: false });
      return;
    }

    if (onFilesSelect) {
      onFilesSelect(acceptedFiles);
    } else if (onFileSelect) {
      onFileSelect(acceptedFiles[0]);
    }

    this.setState({ isDragActive: false });
  };
}

interface Props {
  onFileSelect?: (file: File) => void;
  onFilesSelect?: (files: File[]) => void;
  disabled?: boolean;
  dialog?: boolean;
}

class State {
  isDragActive: boolean = false;
}

export default AchFileUpload;
